import { yupResolver } from '@hookform/resolvers/yup';
import { InputAdornment, Stack, Typography } from '@mui/material';
import { InputField } from 'components';
import { NotifyService } from 'config';
import { STORAGE_KEY } from 'constants/global';
import { ButtonSubmit, PageTitle } from 'features/auth/styles';
import { useDebounce } from 'hooks';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_ROUTES } from 'routers/routes';
import { authApi } from 'services';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setGlobalLoading } from 'store/reducers/global';
import { setLocalStorage } from 'utils';
import * as yup from 'yup';
import LogoAuth from '../../components/LogoAuth';
import { ResendBox, ResendText, SentText } from './styles';

interface ICheckCodeForm {
  code: string;
}

const RESEND_DURATION = 60; // seconds

const schemaQuery = yup
  .object({
    email: yup.string().email().required(),
  })
  .required();

const schema = yup
  .object({
    code: yup.string().length(6, 'Incorrect OTP').required('OTP is required'),
  })
  .required();

export const CheckCode = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { globalLoading } = useAppSelector((state) => state.global);
  const [isSentOTP, setIsSentOTP] = useState(false);
  const [countDown, setCountDown] = useState(RESEND_DURATION);
  const [resetCountDown, setResetCountDown] = useState(0);

  const queryParams: {
    email?: string;
  } = queryString.parse(location.search);

  useEffect(() => {
    const isValidQuery = schemaQuery.isValidSync(queryParams);
    if (!isValidQuery) navigate(APP_ROUTES.AUTH.FORGOT_PASSWORD.to);
  }, [navigate, queryParams]);

  const { handleSubmit, control, setValue } = useForm<ICheckCodeForm>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setCountDown(RESEND_DURATION);
    const interval = setInterval(() => {
      setCountDown((pre) => {
        if (pre <= 1) {
          clearInterval(interval);
          return 0;
        }
        return pre - 1;
      });
    }, 1_000);
    return () => clearInterval(interval);
  }, [resetCountDown]);

  const onResend = useDebounce(async () => {
    if (countDown || globalLoading) return;
    setIsSentOTP(false);
    dispatch(setGlobalLoading(true));
    await authApi
      .forgotPassword({
        email: queryParams.email,
      })
      .then(() => {
        setIsSentOTP(true);
        setResetCountDown((pre) => pre + 1);
        NotifyService.success('Success');
        setValue('code', '');
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => dispatch(setGlobalLoading(false)));
  }, 500);

  const onSubmit = useDebounce(async (data: ICheckCodeForm) => {
    dispatch(setGlobalLoading(true));
    await authApi
      .checkCode({ code: data.code, email: queryParams.email })
      .then(({ data: { data } }) => {
        setLocalStorage(STORAGE_KEY.FORGOT_PASSWORD.ACCESS_TOKEN, data.token);
        NotifyService.success('Success');
        navigate(APP_ROUTES.AUTH.RESET_PASSWORD.to);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => dispatch(setGlobalLoading(false)));
  }, 500);

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)}>
      <LogoAuth />
      <PageTitle variant="h1" mb={1}>
        Account Verification
      </PageTitle>
      <Typography mb={2} mt={1.25} variant="Web_Label_14" color="grey2.dark">
        OTP has been sent to {queryParams.email}
      </Typography>
      <InputField
        numberOnly
        control={control}
        name="code"
        placeholder="Enter OTP"
        endAdornment={
          <InputAdornment position="end">
            <ResendBox>
              <ResendText
                $ready={!countDown && !globalLoading}
                onClick={onResend}
              >
                {countDown ? `${countDown}s` : 'Resend'}
              </ResendText>
            </ResendBox>
          </InputAdornment>
        }
      />
      {isSentOTP && <SentText>OTP has been resent.</SentText>}
      <Stack
        mt={3}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
      >
        <ButtonSubmit color="primary">Verify</ButtonSubmit>
      </Stack>
    </Stack>
  );
};

export default CheckCode;
